import type {
  CmmdBoardTask,
  CmmdProjectMilestone,
  CmmdTaskStatus,
  CmmdTaskWorkflow,
} from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import type { ProjectTaskGroupBy, ProjectTaskSort } from "~/views/viewDefinition";

/** Shared props for the List and Board views of a project's tasks. */
export interface ProjectTaskViewProps {
  readonly tasks: readonly CmmdBoardTask[];
  /** Forge-only planning mode that presents backlog work as milestone-grouped rows. */
  readonly backlogView?: boolean;
  /** Prebuilt task→thread reverse index (from buildTaskThreadIndex). */
  readonly threadIndex: ReadonlyMap<string, SidebarThreadSummary>;
  readonly pendingTaskIds: ReadonlySet<number>;
  /** Tasks with a durable root conversation attached in CMMD. */
  readonly conversationTaskIds?: ReadonlySet<number>;
  readonly onOpenTask: (taskId: number) => void;
  readonly onSetStatus: (taskId: number, status: CmmdTaskStatus, workflowStageId?: string) => void;
  /** Open the live Forge thread executing this task (resolved by anchor). */
  readonly onOpenThread?: ((taskId: number) => void) | undefined;
  /** Project work-item prefix for Linear-style IDs (`CDT-261`). */
  readonly workItemPrefix?: string | null | undefined;
  /** Open the create composer for a status group (list `+` control). */
  readonly onCreateInGroup?: ((statusKey: string) => void) | undefined;
  /** Open the create composer for an exact milestone x status board cell. */
  readonly onCreateInLane?: ((target: ProjectTaskCreateTarget) => void) | undefined;
  /** Open the canonical editor for an exact milestone from grouped planning UI. */
  readonly onManageMilestone?: ((milestoneId: number) => void) | undefined;
  readonly groupBy?: ProjectTaskGroupBy;
  readonly milestones?: readonly CmmdProjectMilestone[];
  /** Controlled in production so list sorting participates in URL and last-view state. */
  readonly taskSort?: ProjectTaskSort | null;
  readonly onTaskSortChange?: ((sort: ProjectTaskSort | null) => void) | undefined;
  readonly workflow?: CmmdTaskWorkflow | null;
  /** Workflow authority is unavailable, so task mutations are read-only. */
  readonly mutationDisabled?: boolean;
}

export interface ProjectTaskCreateTarget {
  readonly status: CmmdTaskStatus;
  readonly milestoneId: number | null;
  readonly workflowStageId?: string;
}

export const EMPTY_TASK_CONVERSATION_IDS: ReadonlySet<number> = new Set<number>();
export const EMPTY_PROJECT_MILESTONES: readonly CmmdProjectMilestone[] = [];

export interface ProjectTaskPositionMove {
  readonly toStatus: CmmdTaskStatus;
  readonly toStageId?: string;
  readonly beforeTaskId?: number;
  readonly afterTaskId?: number;
}

export type ProjectTaskBoardMove =
  | { readonly kind: "position"; readonly move: ProjectTaskPositionMove }
  | { readonly kind: "milestone"; readonly milestoneId: number | null }
  | {
      readonly kind: "lane-position";
      readonly milestoneId: number | null;
      readonly move: ProjectTaskPositionMove;
    };

/** Board props add reordering/move on top of the shared view props. */
export interface ProjectTaskBoardViewProps extends ProjectTaskViewProps {
  readonly onMoveTask: (taskId: number, move: ProjectTaskBoardMove) => void;
  /** Search may surface nested work items that are not root-order siblings. */
  readonly reorderingDisabled?: boolean;
}

function compareTaskOrder(a: CmmdBoardTask, b: CmmdBoardTask): number {
  const aHasCustomOrder = a.order > 0;
  const bHasCustomOrder = b.order > 0;

  if (aHasCustomOrder !== bHasCustomOrder) return aHasCustomOrder ? -1 : 1;
  if (aHasCustomOrder && a.order !== b.order) return a.order - b.order;
  const aTailOrder = a.order < 0 ? -a.order : a.id;
  const bTailOrder = b.order < 0 ? -b.order : b.id;
  return aTailOrder - bTailOrder;
}

/**
 * Sort explicitly ranked tasks first, then untouched tasks by ascending ID.
 * A new zero-ranked task is appended to a custom lane without disturbing its
 * persisted drag order; an untouched lane is deterministic oldest-ID first.
 */
export function activeTasksInOrder(tasks: readonly CmmdBoardTask[]): readonly CmmdBoardTask[] {
  return tasks.filter((task) => !task.archived).toSorted(compareTaskOrder);
}

/** Flatten the complete task hierarchy for detail navigation and dependency lookups. */
export function flattenTaskTree(tasks: readonly CmmdBoardTask[]): readonly CmmdBoardTask[] {
  return tasks.flatMap((task) => [task, ...flattenTaskTree(task.children)]);
}

export const TASK_STATUS_OPTIONS: readonly CmmdTaskStatus[] = [
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "blocked",
  "done",
  "cancelled",
];

/** Coerce a free-string board status to a known CmmdTaskStatus (fallback todo). */
export function normalizeTaskStatus(status: string): CmmdTaskStatus {
  return (TASK_STATUS_OPTIONS as readonly string[]).includes(status)
    ? (status as CmmdTaskStatus)
    : "todo";
}
